/**
 * Maps a user-selectable seasonal `data-theme` to the decoration it layers: an
 * optional backdrop and optional ambient effects. The palette itself lives in
 * `index.css`; this only declares what extra scenery rides along with a theme.
 * Adding a seasonal theme is a pure-data change here plus its registry entries.
 */

/** Identifier for an ambient effect registered in the effects registry. */
export type EffectId = 'snow' | 'bats' | 'petals' | 'leaves' | 'fireworks' | 'shamrocks';

/** Identifier for a seasonal background registered in the backgrounds registry. */
export type BackgroundId =
  | 'snowdrifts'
  | 'halloween-night'
  | 'sakura-fuji'
  | 'autumn-hills'
  | 'liberty-skyline'
  | 'shamrock-bunting';

/** The scenery a seasonal theme layers on top of its palette. */
export interface SeasonalDecoration {
  /** Background decoration rendered behind content. */
  background?: BackgroundId;
  /** Ambient effects layered over content. */
  effects?: EffectId[];
}

/**
 * Decoration keyed by `data-theme` id. A theme absent from this map (the base
 * themes like `classic`) simply renders no backdrop or effects.
 */
export const SEASONAL_DECORATIONS: Record<string, SeasonalDecoration> = {
  winter: { background: 'snowdrifts', effects: ['snow'] },
  halloween: { background: 'halloween-night', effects: ['bats'] },
  'cherry-blossom': { background: 'sakura-fuji', effects: ['petals'] },
  fall: { background: 'autumn-hills', effects: ['leaves'] },
  patriotic: { background: 'liberty-skyline', effects: ['fireworks'] },
  'st-patricks': { background: 'shamrock-bunting', effects: ['shamrocks'] },
};
